import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Radio } from "lucide-react";
import { PARTY } from "../config/party";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1721133073235-e4b5facb27fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const ACTS = PARTY.acts;
const RELEASE = new Date(new Date(PARTY.dateISO).getTime() - 4 * 24 * 3600 * 1000);
const pad2 = (n) => String(n).padStart(2, "0");

export const Lineup = () => {
  const [img, setImg] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const released = now >= RELEASE.getTime();
  const secs = Math.max(0, Math.floor((RELEASE.getTime() - now) / 1000));
  const cd = {
    d: Math.floor(secs / 86400),
    h: Math.floor((secs % 86400) / 3600),
    m: Math.floor((secs % 3600) / 60),
    s: secs % 60,
  };

  useEffect(() => {
    axios
      .get(`${API}/image?slot=lineup`)
      .then((res) => setImg(res.data.image))
      .catch(() => {});
  }, []);

  return (
  <section className="relative z-10 px-6 sm:px-12 lg:px-24 py-28" data-testid="lineup-section">
    <motion.p
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      className="text-[11px] tracking-[0.5em] text-[#00F0FF] uppercase mb-4"
    >
      Transmission schedule
    </motion.p>
    <motion.h2
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: 0.1 }}
      className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white border-l-4 border-[#FF0055] pl-5 mb-16"
    >
      The lineup
    </motion.h2>

    <div className="grid lg:grid-cols-5 gap-8 items-stretch">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9 }}
        className="lg:col-span-2 relative overflow-hidden border border-white/10 min-h-[320px]"
      >
        <img
          src={img ? `${BACKEND}${img.url}` : DEFAULT_IMAGE}
          alt="Laser beams cutting through warehouse darkness"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
          data-testid="lineup-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08080C] via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#39FF14]">
          <Radio size={12} className="animate-pulse" /> Live from the Rat Nest
        </div>
      </motion.div>

      {released ? (
        <div className="lg:col-span-3 divide-y divide-white/10 border border-white/10 bg-[#111118]/60 backdrop-blur-xl">
          {ACTS.map((act, i) => (
            <motion.div
              key={act.name}
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group flex items-center justify-between gap-4 px-6 py-6 hover:bg-[#39FF14]/5 transition-colors duration-300"
              data-testid={`lineup-act-${i}`}
            >
              <div>
                <div className="font-bebas text-2xl sm:text-3xl tracking-wide text-white group-hover:text-[#39FF14] transition-colors duration-300">
                  {act.name}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-lg sm:text-xl text-[#00F0FF] tabular-nums">{act.time}</div>
                {act.tag && (
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#FF0055] mt-1">{act.tag}</div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div
          className="lg:col-span-3 border border-[rgba(255,0,85,0.35)] bg-[#08080C]/80 backdrop-blur-xl flex flex-col items-center justify-center py-20 px-6 text-center"
          data-testid="lineup-locked"
        >
          <Lock size={22} className="text-[#FF0055] mb-5" />
          <div className="font-display text-lg sm:text-xl font-bold uppercase tracking-widest text-white">
            Lineup encrypted
          </div>
          <div
            className="font-display text-3xl sm:text-4xl font-bold text-[#39FF14] tabular-nums mt-5 glitchy"
            data-testid="lineup-countdown"
          >
            {pad2(cd.d)}<span className="text-[#FF0055]">:</span>{pad2(cd.h)}
            <span className="text-[#FF0055]">:</span>{pad2(cd.m)}
            <span className="text-[#FF0055]">:</span>{pad2(cd.s)}
          </div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-5">
            Decrypts 20.09 - four days before the rave
          </p>
        </div>
      )}
    </div>
  </section>
  );
};
